import { GcpErrorReporter } from "@kaggle/core";
import { featureFlags, getSessionClaims, getSessionTokenFromCookie } from "@kaggle/sdk";
var MOCK_REPORTER = {
    report: function(error) {
        return(// eslint-disable-next-line no-console
        console.error("[Error reporter] error.message: " + "".concat(error.message, "\nerror.stack: ").concat(error.stack)));
    }
};
var errorReporter;
function isErrorReporterDisabled() {
    return !featureFlags.frontendErrorReporting;
}
function isDevBuild() {
    return process.env.NODE_ENV !== "production";
}
function getErrorSampleRate() {
    var errorSampleRate = Number(featureFlags.frontendErrorReportingSampleRate);
    if (isNaN(errorSampleRate) || errorSampleRate > 1 || errorSampleRate < 0) {
        return undefined;
    }
    return errorSampleRate;
}
/**
 * Initializes the error reporter ahead of usage. This is primarily useful if
 * you wish to assign a custom version modifier (i.e. "reporting bucket").
 * Each unique reporting version can be filtered in go/kaggle-fe-errors.
 *
 * @param versionModifier A string representing a suffix for the reporting
 *     version.
 */ export function initErrorReporter(versionModifier) {
    if (isErrorReporterDisabled()) {
        return;
    }
    // Don't send error reports for dev builds to reduce noise
    if (isDevBuild()) {
        errorReporter = MOCK_REPORTER;
        return;
    }
    var sessionClaims = getSessionClaims(getSessionTokenFromCookie());
    if (sessionClaims) {
        var ref;
        var ref1;
        var gcpErrorReporter = new GcpErrorReporter({
            key: sessionClaims.sdak,
            projectId: sessionClaims.pid,
            service: sessionClaims.svc,
            version: versionModifier ? "ci-".concat(versionModifier) : "ci",
            userId: (ref1 = (ref = sessionClaims.uid) === null || ref === void 0 ? void 0 : ref.toString()) !== null && ref1 !== void 0 ? ref1 : "",
            sampleRate: getErrorSampleRate()
        });
        errorReporter = gcpErrorReporter;
    } else {
        // Not expected, but fallback on something that won't break.
        errorReporter = MOCK_REPORTER;
    }
}
/**
 * Retrieves the error reporter singleton, initializing it if it has not
 * already been initialized for a given page.
 *
 * @example <caption>Canonical usage in a try / catch block</caption>
 * import { getErrorReporter } from "@kaggle/components";
 *
 * try {
 *   thingThatMayThrow();
 * } catch(err) {
 *   if (err instanceof Error) {
 *     getErrorReporter()?.report(err);
 *   }
 * }
 *
 * @example <caption>Report an error with a custom message</caption>
 * import { reportError } from "@kaggle/components";
 *
 * try {
 *   thingThatMayThrow();
 * } catch(err) {
 *   if (err instanceof Error) {
 *     reportError("Dev friendly custom message. Check X if this broke.", err);
 *   }
 * }
 *
 * @example <caption>Report an error without an Error object</caption>
 * import { reportError } from "@kaggle/components";
 *
 * if (somethingThatShouldNeverHappen) {
 *   reportError("If this happens we should investigate X.");
 * }
 */ export function getErrorReporter() {
    if (isErrorReporterDisabled()) {
        return null;
    }
    if (errorReporter) {
        return errorReporter;
    }
    // Error reporter is not initialized yet.
    initErrorReporter();
    return errorReporter;
}
/** Shorthand for reporting errors with a custom string message. */ export var reportError = function(customMessage, error) {
    var ref;
    return (ref = getErrorReporter()) === null || ref === void 0 ? void 0 : ref.report(new Error("".concat(customMessage, "\n").concat(error ? "".concat(error.message, " \nerror.stack: ").concat(error.stack) : "")));
};
